import React, { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  enableRetry?: boolean;
  retryButtonText?: string;
  showDetails?: boolean;
  className?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
}

/**
 * Error Boundary component that catches JavaScript errors in its child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private retryCount = 0;
  private maxRetries = 3;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: this.generateErrorId()
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: ErrorBoundary.generateErrorId()
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to monitoring service
    this.logError(error, errorInfo);

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Update state with error info
    this.setState({
      error,
      errorInfo
    });
  }

  private generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  static generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private logError(error: Error, errorInfo: ErrorInfo): void {
    const errorData = {
      errorId: this.state.errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      retryCount: this.retryCount
    };

    // Log to console for development
    console.error('Error Boundary caught an error:', errorData);

    // In production, this would send to a monitoring service like Sentry
    // Example: Sentry.captureException(error, { extra: errorData });

    // Store in localStorage for debugging
    try {
      const errorHistory = JSON.parse(localStorage.getItem('jiraz_error_history') || '[]');
      errorHistory.push(errorData);

      // Keep only last 10 errors
      if (errorHistory.length > 10) {
        errorHistory.shift();
      }

      localStorage.setItem('jiraz_error_history', JSON.stringify(errorHistory));
    } catch (e) {
      console.warn('Failed to store error in localStorage:', e);
    }
  }

  private handleRetry = (): void => {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      console.log(`Retrying after error (attempt ${this.retryCount}/${this.maxRetries})`);

      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        errorId: this.generateErrorId()
      });
    } else {
      console.warn('Maximum retry attempts reached');
      // Could show a different message or redirect to a safe page
    }
  };

  private handleReset = (): void => {
    this.retryCount = 0;
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: this.generateErrorId()
    });
  };

  private canRetry(): boolean {
    return this.props.enableRetry !== false && this.retryCount < this.maxRetries;
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className={`error-boundary-fallback ${this.props.className || ''}`}>
          <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg border border-red-200">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Something went wrong
                </h3>
              </div>
            </div>

            <div className="text-sm text-red-700 mb-4">
              <p>
                We're sorry, but something unexpected happened.
                {this.canRetry() && ' You can try again or refresh the page.'}
                {!this.canRetry() && ' Please refresh the page to continue.'}
              </p>
            </div>

            {/* Error details (optional) */}
            {this.props.showDetails && this.state.error && (
              <details className="mb-4 p-3 bg-red-50 rounded border border-red-200">
                <summary className="text-sm font-medium text-red-800 cursor-pointer">
                  Error Details
                </summary>
                <div className="mt-2 text-xs text-red-600">
                  <p><strong>Error ID:</strong> {this.state.errorId}</p>
                  <p><strong>Message:</strong> {this.state.error.message}</p>
                  {this.state.retryCount > 0 && (
                    <p><strong>Retry attempts:</strong> {this.state.retryCount}/{this.maxRetries}</p>
                  )}
                </div>
              </details>
            )}

            {/* Action buttons */}
            <div className="flex space-x-3">
              {this.canRetry() && (
                <button
                  onClick={this.handleRetry}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  {this.props.retryButtonText || 'Try Again'}
                </button>
              )}

              <button
                onClick={() => window.location.reload()}
                className="flex-1 bg-gray-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Refresh Page
              </button>
            </div>

            {/* Additional recovery options */}
            <div className="mt-4 pt-4 border-t border-red-200">
              <p className="text-xs text-red-600 mb-2">
                If this problem persists, you can:
              </p>
              <ul className="text-xs text-red-600 space-y-1">
                <li>• Try clearing your browser cache</li>
                <li>• Check your internet connection</li>
                <li>• Contact support if the issue continues</li>
              </ul>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * HOC to wrap a component with an ErrorBoundary
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
): React.ComponentType<P> {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}

/**
 * Hook to get error history from localStorage
 */
export function useErrorHistory(): Array<any> {
  const [errors, setErrors] = React.useState<Array<any>>([]);

  React.useEffect(() => {
    try {
      const history = JSON.parse(localStorage.getItem('jiraz_error_history') || '[]');
      setErrors(history);
    } catch (e) {
      console.warn('Failed to read error history:', e);
    }
  }, []);

  const clearHistory = React.useCallback(() => {
    localStorage.removeItem('jiraz_error_history');
    setErrors([]);
  }, []);

  return { errors, clearHistory };
}
